import { Link, useNavigate } from "react-router-dom";
import {
  MODE_META,
  ORIGIN_META,
  fmt,
  type Avatar,
  type OriginKey,
} from "@/lib/analytics/demo-data";
import { useAnalyticsData } from "@/lib/analytics/range";
import { Icon } from "./Icon";

export function ModeBadge({ mode }: { mode: Avatar["mode"] }) {
  const m = MODE_META[mode];
  return (
    <span className={`mode ${m.cls}`}>
      <Icon name={m.icon} />
      {m.label}
    </span>
  );
}

export function OriginTag({ origin }: { origin: OriginKey }) {
  const m = ORIGIN_META[origin];
  return (
    <span className={`origin-tag ${origin}`}>
      <Icon name={m.icon} className="origin-ico" />
      {m.label}
    </span>
  );
}

export function Kpi({
  icon,
  label,
  value,
  unit,
  sub,
}: {
  icon: string;
  label: string;
  value: React.ReactNode;
  unit?: string | undefined;
  sub: string;
}) {
  return (
    <div className="kpi">
      <div className="k-top">
        <Icon name={icon} /> {label}
      </div>
      <div className="k-val">
        {value}
        {unit && <span className="unit">{unit}</span>}
      </div>
      <div className="k-sub">{sub}</div>
    </div>
  );
}

export function Donut({ pass }: { pass: number }) {
  const c = 2 * Math.PI * 52;
  const off = c * (1 - pass / 100);
  return (
    <div className="donut">
      <svg width="132" height="132" viewBox="0 0 132 132">
        <circle cx="66" cy="66" r="52" fill="none" stroke="#F5B9C2" strokeWidth="14" />
        <circle
          cx="66"
          cy="66"
          r="52"
          fill="none"
          stroke="#3A57FA"
          strokeWidth="14"
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={off}
          transform="rotate(-90 66 66)"
        />
      </svg>
      <div className="center">
        <div className="d-val">{pass}%</div>
        <div className="d-lab">pass rate</div>
      </div>
    </div>
  );
}

export function OriginBars({
  data,
  total,
}: {
  data: Partial<Record<OriginKey, number>>;
  total: number;
}) {
  const keys = (Object.keys(data) as OriginKey[]).filter((k) => (data[k] ?? 0) > 0);
  const max = Math.max(...keys.map((k) => data[k] ?? 0));
  return (
    <>
      {keys.map((k) => (
        <div className="obar" key={k}>
          <div className="o-label">{ORIGIN_META[k].label}</div>
          <div className="o-track">
            <div
              className="o-fill"
              style={{
                width: `${Math.round(((data[k] ?? 0) / max) * 100)}%`,
                background: ORIGIN_META[k].color,
              }}
            />
          </div>
          <div className="o-val">
            {fmt(data[k] ?? 0)} <span>({Math.round(((data[k] ?? 0) / total) * 100)}%)</span>
          </div>
        </div>
      ))}
    </>
  );
}

export function WeeklyChart() {
  const { weeks: WEEKS, minutes: WK_MINUTES, learners: WK_LEARNERS } = useAnalyticsData();
  const W = 1000, H = 220, x0 = 54, x1 = 944, y0 = 18, y1 = 176;
  const maxMin = Math.ceil(Math.max(...WK_MINUTES) / 100) * 100;
  const maxUsr = Math.ceil(Math.max(...WK_LEARNERS) / 20) * 20;
  const n = WEEKS.length;
  const band = (x1 - x0) / n;
  const bw = Math.min(26, band * 0.36);
  const cx = (i: number) => x0 + band * i + band / 2;
  const yMin = (v: number) => y1 - (v / maxMin) * (y1 - y0);
  const yUsr = (v: number) => y1 - (v / maxUsr) * (y1 - y0);

  return (
    <svg
      className="linechart"
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="xMidYMid meet"
      role="img"
      aria-label="Weekly streaming minutes and unique learners"
    >
      {[0, 0.25, 0.5, 0.75, 1].map((f) => {
        const y = y1 - (y1 - y0) * f;
        return (
          <g key={f}>
            <line x1={x0} y1={y} x2={x1} y2={y} stroke="#EDEEF3" strokeWidth="1" />
            <text x={x0 - 11} y={y + 3.5} textAnchor="end" fontSize="10" fill="#8D92A5">
              {Math.round(maxMin * f)}
            </text>
            <text x={x1 + 11} y={y + 3.5} textAnchor="start" fontSize="10" fill="#854CE0">
              {Math.round(maxUsr * f)}
            </text>
          </g>
        );
      })}
      {WK_MINUTES.map((v, i) => (
        <rect
          key={i}
          x={cx(i) - bw / 2}
          y={yMin(v)}
          width={bw}
          height={y1 - yMin(v)}
          rx="4"
          fill="#8597FC"
        />
      ))}
      <polyline
        points={WK_LEARNERS.map((v, i) => `${cx(i)},${yUsr(v)}`).join(" ")}
        fill="none"
        stroke="#854CE0"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      {WK_LEARNERS.map((v, i) => (
        <circle key={i} cx={cx(i)} cy={yUsr(v)} r="3" fill="#fff" stroke="#854CE0" strokeWidth="2">
          <title>{`${v} unique learners · ${WEEKS[i]}`}</title>
        </circle>
      ))}
      {WEEKS.map((w, i) => (
        <text key={w} x={cx(i)} y={y1 + 21} textAnchor="middle" fontSize="10.5" fill="#696F89">
          {w}
        </text>
      ))}
    </svg>
  );
}

export function AvatarsTable() {
  const { avatars: AVATARS } = useAnalyticsData();
  const navigate = useNavigate();

  /** The whole row is the target; nested links/buttons keep their own behaviour. */
  function openAvatar(id: string, e: { target: EventTarget | null }) {
    if (e.target instanceof Element && e.target.closest("a,button")) return;
    navigate(`/trainer/analytics/avatars/${id}`);
  }
  return (
    <div className="tbl-wrap">
      <table>
        <thead>
          <tr>
            <th>Avatar</th>
            <th>Interaction mode</th>
            <th className="num">Sessions</th>
            <th className="num">Av. pass rate</th>
            <th className="num">Pass rate</th>
            <th className="num">Avg score</th>
            <th className="num">Total mins</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {AVATARS.map((a) => (
            <tr
              key={a.id}
              className="clickable"
              role="link"
              tabIndex={0}
              aria-label={`Open ${a.name} analytics`}
              onClick={(e) => openAvatar(a.id, e)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  navigate(`/trainer/analytics/avatars/${a.id}`);
                }
              }}
            >
              <td>
                <Link
                  to={`/trainer/analytics/avatars/${a.id}`}
                  className="cell-name"
                  style={{ color: "inherit", textDecoration: "none" }}
                >
                  <div className="thumb">
                    <Icon name={a.is_scorable ? "verified" : "record_voice_over"} />
                  </div>
                  <div>
                    <div className="nm">{a.name}</div>
                    <div className="sub">
                      {a.is_scorable
                        ? a.pass_criteria != null
                          ? `Scorable · pass ≥ ${a.pass_criteria}%`
                          : "Scorable"
                        : "Not scored"}
                    </div>
                  </div>
                </Link>
              </td>
              <td>
                <ModeBadge mode={a.mode} />
              </td>
              <td className="num">{fmt(a.sessions)}</td>
              <td className="num">
                <span className="mini-bar">
                  <span className="mb-track">
                    <span className="mb-fill" style={{ width: `${a.completionRate}%` }} />
                  </span>
                  {a.completionRate}%
                </span>
              </td>
              <td className="num">
                {a.passRate !== null ? (
                  <span className="pill pass">
                    <Icon name="check" />
                    {a.passRate}%
                  </span>
                ) : (
                  <span className="pill na">—</span>
                )}
              </td>
              <td className="num">
                {a.avgScore !== null ? `${a.avgScore}%` : <span className="dash">—</span>}
              </td>
              <td className="num">{fmt(a.streamingTotal)} min</td>
              <td className="num">
                <Icon name="chevron_right" style={{ color: "var(--ink-3)", fontSize: "20px" }} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
